// src/components/admin/MetricsRecalculator.jsx
import React, { useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../../utils/firebase';

const MetricsRecalculator = ({ salonId }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState(null);

  const addLog = (message) => {
    setLogs(prev => [...prev, message]);
  };

  const getDateOnly = (timestamp) => {
    let date;
    if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else if (timestamp?.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    // Use local date components to avoid UTC timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const recalculateMetrics = async () => {
    setIsProcessing(true);
    setLogs([]);
    setResults(null);

    addLog('='.repeat(60));
    addLog('Starting Customer Metrics Recalculation');
    addLog('='.repeat(60));
    addLog('');

    try {
      // Get all customers for this salon
      addLog(`Fetching customers for salon: ${salonId}...`);
      const customersRef = collection(db, 'customers');
      const customersQuery = query(
        customersRef,
        where('salonId', '==', salonId)
      );
      const customersSnapshot = await getDocs(customersQuery);

      addLog(`Found ${customersSnapshot.size} customers to process\n`);

      if (customersSnapshot.empty) {
        addLog('No customers found for this salon.');
        setResults({ total: 0, updated: 0, skipped: 0, failed: 0 });
        setIsProcessing(false);
        return;
      }

      setProgress({ current: 0, total: customersSnapshot.size });

      let updated = 0;
      let skipped = 0;
      let failed = 0;

      for (let i = 0; i < customersSnapshot.docs.length; i++) {
        const customerDoc = customersSnapshot.docs[i];
        const customerData = customerDoc.data();
        const customerId = customerDoc.id;

        setProgress({ current: i + 1, total: customersSnapshot.size });

        const displayName = customerData.displayName || customerData.email || customerId;
        addLog(`[${i + 1}/${customersSnapshot.size}] Processing: ${displayName}`);

        try {
          // Query all transactions for this customer (without orderBy to avoid index requirement)
          const transactionsRef = collection(db, 'transactions');
          const q = query(
            transactionsRef,
            where('customerEmail', '==', customerData.email),
            where('salonId', '==', salonId)
          );

          const transactionsSnapshot = await getDocs(q);

          if (transactionsSnapshot.empty) {
            addLog(`  ⊘ No transactions found - skipping`);
            skipped++;
            continue;
          }

          // Sort all transactions by createdAt descending
          const allTransactions = [];
          transactionsSnapshot.forEach(doc => {
            allTransactions.push(doc.data());
          });

          allTransactions.sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime; // Descending order (most recent first)
          });

          // Last visit = most recent transaction (any status)
          const mostRecentTransaction = allTransactions[0];
          const lastVisitDate = mostRecentTransaction.createdAt;

          // Total visits = unique dates across all transactions
          const uniqueDates = new Set();
          transactionsSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.createdAt) {
              uniqueDates.add(getDateOnly(data.createdAt));
            }
          });
          const totalVisits = uniqueDates.size;

          // #Revised UI_v2 — calculate spend & lastTransaction from Completed transactions only
          const now = new Date();
          const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

          let totalSpent = 0;
          let totalSpentLast365Days = 0;
          let lastTransactionDate = null;

          allTransactions.forEach(data => {
            if (data.paymentStatus !== 'Completed') return;

            const amount = parseFloat(data.price) || parseFloat(data.totalAmount) || parseFloat(data.amount) || 0;
            totalSpent += amount;

            // Resolve transaction date
            let txDate = null;
            if (data.paidAt?.toDate) txDate = data.paidAt.toDate();
            else if (data.paidAt?.seconds) txDate = new Date(data.paidAt.seconds * 1000);
            else if (data.createdAt?.toDate) txDate = data.createdAt.toDate();
            else if (data.createdAt?.seconds) txDate = new Date(data.createdAt.seconds * 1000);

            if (txDate) {
              if (txDate >= oneYearAgo) totalSpentLast365Days += amount;
              if (!lastTransactionDate || txDate > lastTransactionDate) lastTransactionDate = txDate;
            }
          });

          // Update customer document with all recalculated metrics
          const customerRef = doc(db, 'customers', customerId);
          await updateDoc(customerRef, {
            lastVisit: lastVisitDate,
            'metrics.totalVisits': totalVisits,
            'metrics.totalSpent': totalSpent,
            'metrics.totalSpentLast365Days': totalSpentLast365Days,
            ...(lastTransactionDate ? { 'metrics.lastTransaction': lastTransactionDate } : {}),
            updatedAt: new Date()
          });

          const lastVisitStr = lastVisitDate.toDate
            ? lastVisitDate.toDate().toLocaleDateString()
            : new Date(lastVisitDate.seconds * 1000).toLocaleDateString();

          addLog(`  ✓ Updated - Visits: ${totalVisits}, Spend: $${totalSpent.toFixed(2)}, Last 365d: $${totalSpentLast365Days.toFixed(2)}, Last Txn: ${lastTransactionDate ? lastTransactionDate.toLocaleDateString() : 'none'}, Last Visit: ${lastVisitStr}`);
          updated++;

        } catch (error) {
          addLog(`  ✗ Failed: ${error.message}`);
          console.error(`Error processing ${customerId}:`, error);
          failed++;
        }
      }

      addLog('');
      addLog('='.repeat(60));
      addLog('Recalculation Complete!');
      addLog('='.repeat(60));
      addLog(`Total customers: ${customersSnapshot.size}`);
      addLog(`Successfully updated: ${updated}`);
      addLog(`Skipped (no transactions): ${skipped}`);
      addLog(`Failed: ${failed}`);
      addLog('='.repeat(60));

      setResults({
        total: customersSnapshot.size,
        updated,
        skipped,
        failed
      });

    } catch (error) {
      addLog('');
      addLog(`❌ ERROR: ${error.message}`);
      console.error('Recalculation error:', error);
      setResults({ error: error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
    setResults(null);
    setProgress({ current: 0, total: 0 });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ background: 'white', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginBottom: '20px' }}>🔄 Recalculate Customer Metrics</h2>

        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '4px', marginBottom: '20px', borderLeft: '4px solid #2196f3' }}>
          <strong>What this does:</strong>
          <ul style={{ marginTop: '10px', marginBottom: '5px' }}>
            <li><strong>Last Visit:</strong> Updates to the latest transaction date</li>
            <li><strong>Total Visits:</strong> Counts unique dates from all transactions</li>
            <li><strong>Total Spend:</strong> Sums amounts from Completed transactions only</li>
            <li><strong>Total Spend (Last 365 Days):</strong> Sums Completed transactions in last 365 days</li>
            <li><strong>Last Transaction:</strong> Date of the most recent Completed transaction</li>
          </ul>
        </div>

        <div style={{ background: '#fff3e0', padding: '15px', borderRadius: '4px', marginBottom: '20px', borderLeft: '4px solid #ff9800' }}>
          <strong>⚠️ Important:</strong>
          <ul style={{ marginTop: '10px', marginBottom: '5px' }}>
            <li>This will recalculate metrics for ALL customers in your salon</li>
            <li>Keep this page open until completion</li>
            <li>Refresh the Customer Management page after completion</li>
          </ul>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={recalculateMetrics}
            disabled={isProcessing}
            style={{
              background: isProcessing ? '#ccc' : '#4caf50',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              marginRight: '10px'
            }}
          >
            {isProcessing ? 'Processing...' : 'Start Recalculation'}
          </button>
          <button
            onClick={clearLogs}
            disabled={isProcessing}
            style={{
              background: '#757575',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: '4px',
              cursor: isProcessing ? 'not-allowed' : 'pointer'
            }}
          >
            Clear Output
          </button>
        </div>

        {progress.total > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
              Progress: {progress.current} / {progress.total} customers
            </div>
            <div style={{ width: '100%', height: '30px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                  height: '100%',
                  background: '#4caf50',
                  transition: 'width 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                {Math.round((progress.current / progress.total) * 100)}%
              </div>
            </div>
          </div>
        )}

        {results && (
          <div style={{
            background: results.error ? '#ffebee' : '#e8f5e9',
            padding: '15px',
            borderRadius: '4px',
            marginBottom: '20px',
            borderLeft: `4px solid ${results.error ? '#f44336' : '#4caf50'}`
          }}>
            {results.error ? (
              <div>
                <strong>❌ Error:</strong> {results.error}
              </div>
            ) : (
              <div>
                <strong>✅ Complete!</strong>
                <div style={{ marginTop: '10px' }}>
                  <div>Total Customers: {results.total}</div>
                  <div>Successfully Updated: {results.updated}</div>
                  <div>Skipped (no transactions): {results.skipped}</div>
                  <div>Failed: {results.failed}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {logs.length > 0 && (
          <div style={{
            background: '#f9f9f9',
            padding: '15px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #ddd'
          }}>
            {logs.map((log, index) => (
              <div key={index}>{log}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsRecalculator;
